<script lang="ts" setup>
import type { Charge } from '@/services/order'
import IconStatusError from '@/assets/icons/status-error.svg?component'
import IconStatusSuccess from '@/assets/icons/status-success.svg?component'
import Message from '@/components/Message.vue'
import { formatDate } from '@/lib/formatters/date'

const props = defineProps<{
  charge: Charge
}>()

const presentOrderMessage = "We're having trouble processing your order"

const retryMessage = (date: string) => {
  return `We'll automatically retry on ${formatDate(
    'DD Month',
    new Date(date),
  )}.`
}

let message: string
let showUpdateDetailsPrompt = false
let showRetryMessage = true
switch (props.charge.error_type) {
  case 'CARD_DECLINED':
  case 'PAYMENT_METHOD_DECLINED':
  case 'CARDNUMBER_INCORRECT':
    message = 'Your card was declined.'
    showUpdateDetailsPrompt = true
    break
  case 'INSUFFICIENT_FUNDS':
    message = 'Your payment method had insufficient funds.'
    showUpdateDetailsPrompt = true
    break
  case 'CARD_EXPIRED':
    message = 'Your payment method has expired.'
    showUpdateDetailsPrompt = true
    showRetryMessage = false
    break
  case 'INSUFFICIENT_INVENTORY':
    message = 'One or more items on your order are out of stock.'
    break
  case 'CLOSED_MAX_RETRIES_REACHED':
    message = 'We retried multiple times.'
    showRetryMessage = false
    break
}
</script>

<template>
  <div class="ChargeErrorBanner">
    <Message
      v-if="charge.error_type === 'CARD_UPDATED_NOW_PENDING_NEXT_ATTEMPT'"
      mode="success"
    >
      <template #icon><IconStatusSuccess /></template>
      <p>
        <strong>Thanks for updating your payment details.</strong>
      </p>
      <p v-if="charge.retry_date">
        {{ retryMessage(charge.retry_date) }}
      </p>
    </Message>
    <Message v-else-if="charge.error_type" mode="error">
      <template #icon><IconStatusError /></template>
      <p>
        <strong>{{ presentOrderMessage }}</strong>
      </p>
      <p v-if="message">{{ message }}</p>
      <p v-if="charge.error_type === 'INSUFFICIENT_INVENTORY'">
        <a
          href="/out-of-stock"
          @click.prevent="$router.push('/out-of-stock')"
        >
          Choose what to do with out of stock products
        </a>
      </p>
      <p v-if="showUpdateDetailsPrompt">
        Please check
        <a
          href="/payment-methods"
          @click.prevent="$router.push('/payment-methods')"
        >
          your payment details
        </a>
        and ensure they are up to date. Once you have updated your details, your
        order will be processed the next day.
      </p>
      <p v-if="charge.retry_date && showRetryMessage">
        {{ retryMessage(charge.retry_date) }}
      </p>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p>
        If you would like any help with this issue, please get in touch with us
        at
        <a href="mailto:hello@bowercollective.com">hello@bowercollective.com</a>
      </p>
    </Message>
    <Message v-else mode="warning">
      <p>
        <strong>This charge is still pending</strong>
        <br />This could be due to a payment error or delay. Please check back
        in a few minutes.
      </p>
      <p>
        If you don't see this order go through successfully, please get in touch
        with us at
        <a href="mailto:hello@bowercollective.com">hello@bowercollective.com</a>
      </p>
    </Message>
  </div>
</template>

<style>
.ChargeErrorBanner {
  margin: 10px 0;
}
</style>
